import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Grid,
  Typography,
  AppBar,
  Toolbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DragHandle, Add, Edit, Save, Close } from "@mui/icons-material";
import { DragDropContext as RawDragDropContext, Droppable as RawDroppable, Draggable as RawDraggable } from "react-beautiful-dnd";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { env } from "../../../../env";
import useUpdateAssetData from "../../Hooks/updateAssetsHook";
import { RenderChangeSummary } from "../../utils/renderComponentSummary";
import getKpiChangeSummary from "../../utils/getKpiChangeSummary";
import { useSelector } from "react-redux";
import { sortMapArray } from "../../utils/sortArrayObjectByKey";

const DragDropContext = RawDragDropContext as unknown as React.ComponentType<any>;
const Droppable = RawDroppable as unknown as React.ComponentType<any>;
const Draggable = RawDraggable as unknown as React.ComponentType<any>;
const baseURL = env.microservice_assetsmgt;

interface Kpi {
  kpiName: string;
  kpitag: string | string[];
  kpiUnit: string;
  area: string;
}

interface KpiChange {
  modifiedBy: string;
  modifiedDate: string;
  modifiedChanges: any;
}

interface SubCategoryAttributes {
  subCategoryAsset: string;
  kpiDataPoints: Kpi[];
}

interface KpiSidebarProps {
  open: boolean;
  onClose: () => void;
  defaultKpis?: Kpi[];
  onKpisUpdate: (updatedKpis: Kpi[]) => void;
  assetId: string;
  setUpdatedKpiList?: any;
  updatedKpiList?: any;
  setQueryKey?: any;
  subCategoryData?: { subCategoryAsset: string; kpiDataPoints: Kpi[] }[];
  kpiChangeSummary: KpiChange[];
  enableAreaGrouping?: boolean;
}

const KpiSidebarCategory: React.FC<KpiSidebarProps> = ({
  open,
  onClose,
  onKpisUpdate,
  assetId,
  setUpdatedKpiList = () => {},
  updatedKpiList = [],
  subCategoryData = [],
  kpiChangeSummary = [],
  setQueryKey = () => {},
  enableAreaGrouping = false,
}) => {
  const location = useLocation();
  const [kpis, setKpis] = useState<Kpi[]>([]);
  const [okpis, setOKpis] = useState<Kpi[]>();
  const [kpiChangeHistory, setKpiChangeHistory] =
    useState<KpiChange[]>(kpiChangeSummary);
  const [subCategoryPayload, setSubCategoryPayload] =
    useState<SubCategoryAttributes[]>(subCategoryData);

  useEffect(() => {
    if (Array.isArray(subCategoryData) && subCategoryData.length > 0) {
      const mappedKpis = subCategoryData.flatMap((item) =>
        Array.isArray(item.kpiDataPoints)
          ? item.kpiDataPoints.map((kpi) => ({
              ...kpi,
              area: item.subCategoryAsset || "Unknown Area",
            }))
          : []
      );
      setKpis(mappedKpis.length > 0 ? mappedKpis : []);
      setOKpis(mappedKpis.length > 0 ? mappedKpis : []);
    }
  }, [subCategoryData]);

  const categories = Array.from(
    new Set(subCategoryData.map((item) => item.subCategoryAsset))
  );
  const [newKpi, setNewKpi] = useState<Kpi>({
    kpiName: "",
    kpitag: "",
    kpiUnit: "",
    area: categories[0] || "",
  });
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [queryRefreshKey, setQueryRefreshKey] = useState<string>("");
  const [errorForm, setErrorForm] = useState<string>("");
  const [selectedArea, setSelectedArea] = useState<string>(categories[0] || "");
  const [historyView, setHistoryView] = useState<boolean>(false);
  const usersList = useSelector((state: any) => state.usersList);

  const {
    data: updated_asset_kpi_records,
    error,
    isLoading,
  } = useUpdateAssetData(
    `${baseURL}assets/${assetId}`,
    queryRefreshKey,
    undefined, // no queryParams anymore
    [assetId], // enable only when assetId is available
    { subCategory: subCategoryPayload, kpiChangeSummary: kpiChangeHistory } // your data goes here
  );

  const filteredKpis: any = useMemo(() => {
    return kpis.filter((kpi) => {
      if (!kpi.area || !selectedArea) {
        console.warn("Missing area or selectedArea:", { kpi, selectedArea });
        return false;
      }
      return kpi.area === selectedArea;
    });
  }, [kpis, selectedArea]);

  const handleSubCategoryPayload = (updatedKpiList: Kpi[]) => {
    let uniqueAreas: string[] = [];
    let reorderedSubCategoryData: any = [];

    updatedKpiList.forEach((ele: Kpi) => {
      if (!uniqueAreas.includes(ele?.area)) {
        uniqueAreas.push(ele?.area);
      }
    });

    if (uniqueAreas.length) {
      uniqueAreas
        .sort((a: string, b: string) => {
          if (
            location?.pathname?.includes("hypet") ||
            location?.pathname?.includes("hycap")
          ) {
            return b.localeCompare(a);
          } else {
            return a.localeCompare(b);
          }
        })
        .forEach((areaStr: string) => {
          let kpiDataArray: any = kpis
            .filter((item: Kpi) => areaStr === item?.area)
            .map((kpi: any) => {
              const { area, ...rest } = kpi;

              return rest;
            });
          reorderedSubCategoryData.push({
            subCategoryAsset: areaStr,
            kpiDataPoints: kpiDataArray,
          });
        });
      setSubCategoryPayload(reorderedSubCategoryData);
    }
  };

  const handleAddOrUpdateKpi = () => {
    if (!newKpi.kpiName || !newKpi.kpitag) {
      setErrorForm("KPI Name and KPI Tag are required.");
      return;
    }

    const isDuplicate: boolean = kpis.some((kpi, index) => {
      if (index === editIndex || kpi.area !== newKpi.area) {
        return false;
      }

      const currentTags = Array.isArray(kpi.kpitag)
        ? kpi.kpitag.map((tag) => tag.trim().toLowerCase())
        : [kpi.kpitag.trim().toLowerCase()];

      const newTags = Array.isArray(newKpi.kpitag)
        ? newKpi.kpitag.map((tag) => tag.trim().toLowerCase())
        : [newKpi.kpitag.trim().toLowerCase()];

      return (
        index !== editIndex &&
        kpi.area === newKpi.area &&
        (kpi.kpiName.trim().toLowerCase() ===
          newKpi.kpiName.trim().toLowerCase() ||
          currentTags.some((tag) => newTags.includes(tag)))
      );
    });

    if (isDuplicate) {
      setErrorForm("KPI Name and KPI Tag must be unique within the same area.");
      return;
    }

    setErrorForm("");

    if (editIndex !== null) {
      const updatedKpis = [...kpis];
      updatedKpis[editIndex] = newKpi;
      setKpis(updatedKpis);
      setEditIndex(null);
    } else {
      setKpis([...kpis, newKpi]);
    }

    setNewKpi({ kpiName: "", kpitag: "", kpiUnit: "", area: selectedArea });
    setIsModified(true);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const updatedAreaKpis = [...filteredKpis];
    const [movedItem] = updatedAreaKpis.splice(result.source.index, 1);
    updatedAreaKpis.splice(result.destination.index, 0, movedItem);

    const reorderedKpis = [
      ...kpis.filter((kpi) => kpi.area !== selectedArea),
      ...updatedAreaKpis,
    ];

    setKpis(reorderedKpis);
    setIsModified(true);
  };

  const handleEditKpi = (index: number) => {
    setEditIndex(index);
    setNewKpi(filteredKpis[index]);
  };

  const removeKpi = (index: number) => {
    const updatedFiltered = filteredKpis.filter(
      (_: any, i: number) => i !== index
    );
    const updatedKpis = [
      ...kpis.filter((kpi) => kpi.area !== selectedArea),
      ...updatedFiltered,
    ];
    setKpis(updatedKpis);
    setIsModified(true);
  };

  const handleFinalSave = () => {
    const changeSummary = getKpiChangeSummary(kpis, okpis || []);

    const latestChange: KpiChange = {
      modifiedBy: usersList?.currentUser.user_ad_name, // you can pass a real user
      modifiedDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      modifiedChanges: changeSummary,
    };

    const updatedSummary = [...kpiChangeHistory, latestChange];
    setKpiChangeHistory(updatedSummary);
    onKpisUpdate(kpis);
    setIsModified(false);
    setQueryRefreshKey(
      `asset_kpi_data_update_${assetId}_${moment().valueOf()}`
    );
    setUpdatedKpiList([]);
  };

  useEffect(() => {
    handleSubCategoryPayload(kpis);
  }, [kpis]);

  useEffect(() => {
    if (
      JSON.stringify(updatedKpiList) !==
        JSON.stringify(updated_asset_kpi_records?.data?.data?.subCategory) &&
      !updatedKpiList.length &&
      updated_asset_kpi_records &&
      updated_asset_kpi_records?.data?.data?.subCategory
    ) {
      setUpdatedKpiList(updated_asset_kpi_records?.data?.data?.subCategory);
      setQueryKey(`post_kpi_update_${moment().valueOf()}`);
    }
  }, [updated_asset_kpi_records]);

  useEffect(() => {
    setKpiChangeHistory(kpiChangeSummary);
  }, [kpiChangeSummary]);
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <AppBar
        position="static"
        sx={{
          padding: 2,
          marginTop: "60px",
          backgroundImage: "none",
          backgroundColor: "rgb(39 48 96)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Manage KPIs</Typography>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setHistoryView(!historyView)}
              sx={{ marginRight: 1 }}
            >
              {historyView ? "View KPI list" : "View History"}
            </Button>
            <IconButton edge="end" color="inherit" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      {historyView ? (
        <div
          style={{
            width: 600,
            padding: 8,
            backgroundColor: "rgb(46 57 118)",
            backgroundImage: "none",
            minHeight: "calc(100vh - 130px)",
          }}
        >
          {kpiChangeHistory && kpiChangeHistory.length > 0 ? (
            sortMapArray(kpiChangeHistory)
              .sortByKey("modifiedDate", "desc")
              .map((entry) => (
                <ListItem
                  key={entry.modifiedDate}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 1,
                    backgroundColor: "#232957",
                    borderRadius: "5px",
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <>
                        <Typography component="span" fontWeight="bold">
                          Modified By
                        </Typography>{" "}
                        -- {entry.modifiedBy} {"  "}
                        <Typography component="span" fontWeight="bold">
                          Modified On
                        </Typography>{" "}
                        -- {entry.modifiedDate}
                      </>
                    }
                    secondary={
                      <>
                        <RenderChangeSummary changes={entry.modifiedChanges} />
                      </>
                    }
                  />
                </ListItem>
              ))
          ) : (
            <div
              style={{
                minHeight: "calc(100vh - 150px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography color="textSecondary" align="center">
                No change history available.
              </Typography>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            width: 600,
            padding: 8,
            backgroundColor: "#2e3976",
            minHeight: "calc(100vh - 140px)",
          }}
        >
          <Grid container spacing={1.5} padding={1}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Sub-Category</InputLabel>
                <Select
                  value={newKpi.area}
                  label="Sub-Category"
                  onChange={(e) =>
                    setNewKpi({ ...newKpi, area: e.target.value })
                  }
                >
                  {categories.map((list) => (
                    <MenuItem key={list} value={list}>
                      {list}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={9}>
              <TextField
                label="KPI Name"
                fullWidth
                value={newKpi.kpiName}
                onChange={(e) =>
                  setNewKpi({ ...newKpi, kpiName: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Unit"
                fullWidth
                value={newKpi.kpiUnit}
                onChange={(e) =>
                  setNewKpi({ ...newKpi, kpiUnit: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="KPI Tag"
                fullWidth
                multiline
                value={
                  Array.isArray(newKpi.kpitag)
                    ? newKpi.kpitag.join(", ")
                    : newKpi.kpitag
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setNewKpi({
                    ...newKpi,
                    kpitag: value.includes(",")
                      ? value.split(",").map((tag) => tag.trim())
                      : value,
                  });
                }}
              />
            </Grid>
          </Grid>

          {errorForm && (
            <Typography color="error" sx={{ mt: 1 }}>
              {errorForm}
            </Typography>
          )}

          <Grid container justifyContent="flex-end" spacing={1.5} padding={1}>
            <Grid item>
              <Button
                startIcon={editIndex !== null ? <Save /> : <Add />}
                variant="contained"
                onClick={handleAddOrUpdateKpi}
              >
                {editIndex !== null ? "Save Changes" : "Add KPI"}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() =>
                  setNewKpi({
                    kpiName: "",
                    kpitag: "",
                    kpiUnit: "",
                    area: categories[0] || "",
                  })
                }
              >
                Reset
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={1.5} padding={1}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Render by Sub-Category</InputLabel>
                <Select
                  value={selectedArea}
                  label="Render by Sub-Category"
                  onChange={(e) => setSelectedArea(e.target.value)}
                >
                  {categories.map((list) => (
                    <MenuItem key={list} value={list}>
                      {list}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="kpiList">
                  {(provided: any) => (
                    <List {...provided.droppableProps} ref={provided.innerRef}>
                      {filteredKpis?.map(
                        (
                          kpi: {
                            kpiName: string;
                            kpiUnit: string;
                            kpitag: string;
                            area: string;
                          },
                          index: number
                        ) => (
                          <Draggable
                            key={kpi.kpiName}
                            draggableId={kpi.kpiName}
                            index={index}
                          >
                            {(provided: any) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  backgroundColor: "#232957",
                                  borderRadius: 1,
                                  mb: 1,
                                }}
                              >
                                <DragHandle sx={{ color: "gray" }} />
                                <ListItemText
                                  primary={`${kpi.kpiName} | ${kpi.kpiUnit} | ${kpi.area}`}
                                  secondary={
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {Array.isArray(kpi.kpitag)
                                        ? kpi.kpitag.map(
                                            (tag: string, index: any) => (
                                              <React.Fragment key={index}>
                                                {tag.trim()}
                                                <br />
                                              </React.Fragment>
                                            )
                                          )
                                        : kpi.kpitag}
                                    </Typography>
                                  }
                                />
                                <IconButton
                                  onClick={() => handleEditKpi(index)}
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  onClick={() => removeKpi(index)}
                                  color="error"
                                >
                                  <HighlightOffOutlinedIcon />
                                </IconButton>
                              </ListItem>
                            )}
                          </Draggable>
                        )
                      )}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
            <Grid item xs={12}>
              <Button
                startIcon={<Save />}
                variant="contained"
                color="primary"
                sx={{ mt: 2, width: "100%" }}
                onClick={handleFinalSave}
                disabled={!isModified}
              >
                Save All Changes
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </Drawer>
  );
};

export default KpiSidebarCategory;
